import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PageContainer from '../../../components/Redesing/page-container';
import CustomToast from '../../../components/Redesing/custom-toast';
import IssueFilters, {
  defaultFilters,
  localDayKey,
  toIssueVariables,
} from './IssueFilters';
import {
  AgentTable,
  CategoryTable,
  SummaryStrip,
  SystemTable,
  TagTable,
} from './IssueTables';
import {
  IssueSystemOption,
  downloadIssuesCsv,
  fetchIssueSystems,
  partnerOptions,
  useIssueMetrics,
} from './useIssueMetrics';

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

/**
 * Customer Support's "Issues by tag" report from ant's issueMetrics: what
 * riders and agents tagged in a range, by category, tag, agent and system,
 * with the same numbers as a CSV. Global, like /support/metrics: ant scopes
 * the numbers by the admin's token.
 */
export default function IssuesPage() {
  const [filters, setFilters] = useState(() => defaultFilters());
  const [systems, setSystems] = useState<IssueSystemOption[]>([]);
  const [downloading, setDownloading] = useState(false);
  // Bumped on Refresh so a range that ends today reaches the present again.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    document.title = 'Issues by tag';
    fetchIssueSystems()
      .then(setSystems)
      .catch(() => setSystems([]));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const variables = useMemo(() => toIssueVariables(filters), [filters, tick]);
  const { metrics, loading, error, reload } = useIssueMetrics(variables);
  const partners = useMemo(() => partnerOptions(systems), [systems]);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
    reload();
  }, [reload]);

  const download = async () => {
    setDownloading(true);
    try {
      await downloadIssuesCsv(toIssueVariables(filters));
    } catch (e) {
      CustomToast({
        type: 'error',
        message: `Could not download the CSV. ${errorText(e)}`,
        duration: 6000,
      });
    } finally {
      setDownloading(false);
    }
  };

  const empty = metrics !== null && metrics.sessions === 0 && metrics.riderReports === 0;

  return (
    <PageContainer spaceName="Issues by tag">
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          <Button component={RouterLink} to="/support" startIcon={<ArrowBackIcon />}>
            Support Chat
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            component={RouterLink}
            to="/support/metrics"
            size="small"
            startIcon={<InsightsOutlinedIcon fontSize="small" />}
          >
            Metrics
          </Button>
        </Stack>

        <IssueFilters
          filters={filters}
          onChange={setFilters}
          partners={partners}
          systems={systems}
          loading={loading}
          onReload={refresh}
          downloading={downloading}
          onDownload={download}
        />

        {error && <Alert severity="error">Could not load the report. {error}</Alert>}

        {!metrics && loading && (
          <Stack alignItems="center" sx={{ py: 8 }} data-testid="issues-loading">
            <CircularProgress />
          </Stack>
        )}

        {metrics && (
          <>
            {loading && <LinearProgress sx={{ mt: -1 }} />}
            <Typography variant="caption" color="text.secondary">
              {rangeLabel(metrics.from, metrics.to)} · {zoneLabel()}
            </Typography>
            <SummaryStrip metrics={metrics} />
            {empty && (
              <Alert severity="info">
                No sessions or rider reports in this range with these filters.
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} lg={5}>
                <CategoryTable rows={metrics.byCategory} />
              </Grid>
              <Grid item xs={12} lg={7}>
                <TagTable tags={metrics.byTag} categories={metrics.byCategory} />
              </Grid>
              <Grid item xs={12} lg={6}>
                <AgentTable rows={metrics.byAgent} />
              </Grid>
              <Grid item xs={12} lg={6}>
                <SystemTable rows={metrics.bySystem} />
              </Grid>
            </Grid>
          </>
        )}
      </Stack>
    </PageContainer>
  );
}

/**
 * "Sep 22 – Sep 28, 2026" for the range ant answered for, as the local days
 * the filters asked for.
 */
export function rangeLabel(from: string, to: string): string {
  const start = new Date(from);
  // `to` is exclusive; the last day shown is the one just before it.
  const end = new Date(Date.parse(to) - 1);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
  const day = (d: Date, year: boolean) =>
    d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(year ? { year: 'numeric' } : {}),
    });
  if (localDayKey(start) === localDayKey(end)) return day(start, true);
  return `${day(start, false)} – ${day(end, true)}`;
}

/** "local days (America/New_York)": whose days the range counts. */
export function zoneLabel(): string {
  let zone = '';
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch (_) {
    // No Intl zone: the words alone.
  }
  return zone ? `local days (${zone})` : 'local days';
}
